import { useMutation, useQueryClient } from 'react-query';
import { getSubscriptionBlockUseCases } from '@subscriptions/composition';
import type { UpdateBlockStoresDto } from '@subscriptions/application/dtos';

/**
 * Hook for updating the name and stores of a subscription block.
 *
 * Since the update response is only `{ uuid }`, the detail screen relies on a
 * refetch — so the exact detail query key is invalidated alongside the list.
 * The stores query is also invalidated so the edit multiselect reflects the
 * new grouping next time it opens.
 *
 * @returns Mutation result for updating block stores
 */
export const useUpdateBlockStores = () => {
  const queryClient = useQueryClient();
  const { updateBlockStores } = getSubscriptionBlockUseCases();

  return useMutation<{ uuid: string }, Error, { blockUuid: string; dto: UpdateBlockStoresDto }>(
    ({ blockUuid, dto }) => updateBlockStores.execute(blockUuid, dto),
    {
      onSuccess: (_data, { blockUuid }) => {
        queryClient.invalidateQueries(['subscriptions', 'blocks', 'detail', blockUuid]);
        queryClient.invalidateQueries(['subscriptions', 'blocks', 'list']);
        queryClient.invalidateQueries(['subscriptions', 'stores']);
      },
    },
  );
};
